import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateLeaseDto,
  UpdateLeaseDto,
  LeasesControllerCreateData,
  LeasesControllerFindAllData,
  LeasesControllerFindOneData,
  LeasesControllerUpdateData,
  LeasesControllerRemoveData,
  LeasesControllerUpdateStatusData,
} from "@/common/api/datasources/Api";
import { leasesService, LeaseStatus } from "@/common/api/services/leases";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { LeaseFilters as LeaseFiltersType } from "../types/leases.types";

interface ApiLeaseFilters {
  status?: LeaseStatus;
  propertyUUID?: string;
  tenantUUID?: string;
  managerUUID?: string;
  ownerUUID?: string;
  search?: string;
  minRent?: number;
  maxRent?: number;
  page?: number;
  size?: number;
}

const useLeases = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const getLeases = (filters: LeaseFiltersType = {}) => {
    const apiFilters: ApiLeaseFilters = {
      ...filters,
      status: filters.status,
      page: filters.page,
      size: filters.size || 10,
    };

    return useQuery<LeasesControllerFindAllData, Error>({
      queryKey: ["leases", apiFilters],
      queryFn: () => leasesService.getAllLeases(apiFilters),
    });
  };

  const getLease = (uuid: string) => {
    return useQuery<LeasesControllerFindOneData, Error>({
      queryKey: ["lease", uuid],
      queryFn: () => leasesService.getLeaseByUuid(uuid),
      enabled: !!uuid,
    });
  };

  const createLeaseMutation = useMutation<
    LeasesControllerCreateData,
    Error,
    CreateLeaseDto
  >({
    mutationFn: (data) => leasesService.createLease(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leases"] });
      toast({
        title: t("leases.notifications.created.title"),
        description: t("leases.notifications.created.description"),
      });
    },
    onError: (error) => {
      toast({
        title: t("leases.notifications.error.title"),
        description:
          error.message || t("leases.notifications.error.description"),
        variant: "destructive",
      });
    },
  });

  const updateLeaseMutation = useMutation<
    LeasesControllerUpdateData,
    Error,
    { uuid: string; data: UpdateLeaseDto }
  >({
    mutationFn: ({ uuid, data }) => leasesService.updateLease(uuid, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["leases"] });
      queryClient.invalidateQueries({ queryKey: ["lease", variables.uuid] });
      toast({
        title: t("leases.notifications.updated.title"),
        description: t("leases.notifications.updated.description"),
      });
    },
    onError: (error) => {
      toast({
        title: t("leases.notifications.error.title"),
        description:
          error.message || t("leases.notifications.error.description"),
        variant: "destructive",
      });
    },
  });

  const deleteLeaseMutation = useMutation<
    LeasesControllerRemoveData,
    Error,
    string
  >({
    mutationFn: (uuid) => leasesService.deleteLease(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leases"] });
      toast({
        title: t("leases.notifications.deleted.title"),
        description: t("leases.notifications.deleted.description"),
      });
    },
    onError: (error) => {
      toast({
        title: t("leases.notifications.error.title"),
        description:
          error.message || t("leases.notifications.error.description"),
        variant: "destructive",
      });
    },
  });

  const updateLeaseStatusMutation = useMutation<
    LeasesControllerUpdateStatusData,
    Error,
    { uuid: string; status: LeaseStatus }
  >({
    mutationFn: ({ uuid, status }) => leasesService.updateStatus(uuid, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["leases"] });
      queryClient.invalidateQueries({ queryKey: ["lease", variables.uuid] });
      toast({
        title: t("leases.notifications.statusUpdated.title"),
        description: t("leases.notifications.statusUpdated.description"),
      });
    },
    onError: (error) => {
      toast({
        title: t("leases.notifications.error.title"),
        description:
          error.message || t("leases.notifications.error.description"),
        variant: "destructive",
      });
    },
  });

  return {
    getLeases,
    getLease,
    createLease: createLeaseMutation.mutate,
    updateLease: updateLeaseMutation.mutate,
    deleteLease: deleteLeaseMutation.mutate,
    updateLeaseStatus: updateLeaseStatusMutation.mutate,
    isCreating: createLeaseMutation.isPending,
    isUpdating: updateLeaseMutation.isPending,
    isDeleting: deleteLeaseMutation.isPending,
    isUpdatingStatus: updateLeaseStatusMutation.isPending,
  };
};

export default useLeases;
